import { PromiseExtended, Table } from 'dexie';
import { v4 as uuidv4 } from 'uuid';
import { SyncChanges, SyncRecord, SyncRecordStatus } from '../types/sync-state';
import {
  Certificate,
  CertificateMetadata,
  VENDOR_CODE_PREFIXES,
  OXBRIDGE_METADATA_DEFAULTS,
  BEACONHOUSE_METADATA_DEFAULTS,
  I_SAPS_METADATA_DEFAULTS,
} from './certificates';
import { generateSecureRandomCode } from '@taleemabad/shared/utils';
import { BaseModelForPush } from './base-model';
import {
  IAssessment,
  ICourse,
  ISubmission,
  ITraining,
  ITrainingQuestion,
  ITrainingStatus,
  MediaType,
  ProgressStatus,
  QuestionType,
  TrainingCourseProgressInfo,
  IGrandQuizzes,
  ILevels,
  IGrandQuizStatus,
} from '../types/teachertraining';
import { userDB } from './user';
import {
  DownloadInfo,
  DownloadStatus,
  ModelDownloadName,
} from '../types/downloads';
import { teacherTrainingService } from '../services/teachertraining';
import { downloadRequestModel } from './downloads';
import { downloadManagerService } from '../utils/download-manager';
import {
  analyticsService,
  EventNames,
  fetchTrainingQuestions,
  IQuestion,
} from '..';
import { Network } from '@capacitor/network';
import { LevelAssessmentType } from '@taleemabad/shared';
export type { ITrainingQuestion } from '../types/teachertraining';

const normalizeProfileId = (id: string): string =>
  (id ?? '').replace(/[-_]/g, '').toLowerCase();


/**
 * Strip the cached `thumbnailBase64` blob from a course record before it
 * is returned from a query.
 *
 * Background: every course row in `coursesV2` carries a base64-encoded
 * thumbnail (see `bulkImageDownloadFromUrlToBase64` in api/teachertraining.ts).
 * Each blob is ~150-300 KB, and a fully-synced training catalog has 100+
 * courses. Methods like `getCourses().toArray()` then materialise every blob
 * into V8 memory simultaneously — pushing total JS heap to ~800 MB on the
 * Trainings → Courses screen and triggering OOM crashes on low-end Android.
 *
 * The base64 data is **never read by any UI component** (the codebase only
 * ever writes it; rendering paths use `thumbnailUrl` directly). Stripping it
 * at the read boundary keeps the IndexedDB rows untouched while making the
 * in-memory shape cheap.
 */
export const stripCourseThumbnailBase64 = (
  record: CourseRecord,
): CourseRecord => {
  if (record.thumbnailBase64 === undefined) return record;
  const { thumbnailBase64: _drop, ...rest } = record;
  return rest as CourseRecord;
};

// Increment SchemaVersion whenever schema is changed
// v8: old table names (courses, trainings) kept for upgrade migration
const SCHEMA_V8 = {
  courses: 'id, index, type',
  trainings: 'id, index, course',
  questions: 'id, index, training',
  trainingStatusV2: '++id, [training+courseId+participant], status, syncStatus',
  submissionsV2: '++id, training, syncStatus, score',
  assessmentsV2: '++id, training, grandQuiz, syncStatus, score, totalScore',
  grandQuizzes: '[profileId+id], id, level, status, syncStatus',
  grandQuizzesQuestions: 'id',
  levels: '[profileId+id], id',
};

// v9: add V2 tables with compound PKs; old tables kept for data migration
const SCHEMA_V9 = {
  ...SCHEMA_V8,
  coursesV2: '[profileId+id], id, index, type',
  trainingsV2: '[profileId+id], id, index, course',
};

// v10: add certificates table for Oxbridge and other vendor certificates
const SCHEMA_V10 = {
  ...SCHEMA_V9,
  certificates: 'id, participant, profile, course, vendor, issued_at',
};

// v11: add vendor index to questions table for vendor-specific question filtering
export const SCHEMA_V11 = {
  ...SCHEMA_V10,
  questions: 'id, index, training, vendor',
};

// v12: add profileId index to assessmentsV2 for multi-profile isolation
const SCHEMA_V12 = {
  ...SCHEMA_V11,
  assessmentsV2:
    '++id, profileId, training, grandQuiz, syncStatus, score, totalScore',
};

// v13: fix trainingStatusV2 compound index — was [training+courseId+profile] but
// records store the field as `participant`, making the index dead. Corrected to
// [training+courseId+participant]. Dexie auto-reindexes all existing rows on upgrade.
const SCHEMA_V13 = {
  ...SCHEMA_V12,
  trainingStatusV2: '++id, [training+courseId+participant], status, syncStatus',
};

export const SCHEMA_VERSION = 13;

export interface CourseRecord extends ICourse, SyncRecord {}
export interface TrainingRecord extends ITraining, SyncRecord {}
export interface TrainingQuestionRecord extends ITrainingQuestion, SyncRecord {}
export interface TrainingStatusRecord extends ITrainingStatus, SyncRecord {}
export interface SubmissionRecord extends ISubmission, SyncRecord {}
export interface AssessmentRecord extends IAssessment, SyncRecord {}
export interface GrandQuizzesRecord extends IGrandQuizzes, SyncRecord {}
export interface LevelsRecord extends ILevels, SyncRecord {}

class TeacherTrainingDB extends BaseModelForPush {
  coursesV2!: Table<CourseRecord>;
  trainingsV2!: Table<TrainingRecord>;
  questions!: Table<TrainingQuestionRecord>;
  trainingStatusV2!: Table<TrainingStatusRecord>;
  submissionsV2!: Table<SubmissionRecord>;
  assessmentsV2!: Table<AssessmentRecord>;
  grandQuizzes!: Table<GrandQuizzesRecord>;
  grandQuizzesQuestions!: Table<ITrainingQuestion>;
  levels!: Table<LevelsRecord>;
  certificates!: Table<Certificate>;

  constructor() {
    super('teachertrainingdb');
    this.version(8).stores(SCHEMA_V8);
    this.version(9)
      .stores(SCHEMA_V9)
      .upgrade(async (tx) => {
        const oldCourses = await tx.table('courses').toArray();
        if (oldCourses.length > 0) {
          await tx
            .table('coursesV2')
            .bulkPut(oldCourses.filter((r: CourseRecord) => r.profileId));
        }
        const oldTrainings = await tx.table('trainings').toArray();
        if (oldTrainings.length > 0) {
          await tx
            .table('trainingsV2')
            .bulkPut(oldTrainings.filter((r: TrainingRecord) => r.profileId));
        }
      });

    // v10: add certificates table — no migration needed, new empty table coexists with v9 data
    this.version(10).stores(SCHEMA_V10);

    // v11: add vendor index to questions — no migration needed, vendor is optional on existing records
    this.version(11).stores(SCHEMA_V11);

    // v12: add profileId index to assessmentsV2 — no data migration needed, index added for filtering
    this.version(12).stores(SCHEMA_V12);

    // v13: fix trainingStatusV2 compound index from [training+courseId+profile] to
    // [training+courseId+participant]. The `profile` key was a typo — records store
    // the field as `participant`. Dexie auto-reindexes all existing rows on upgrade.
    this.version(SCHEMA_VERSION).stores(SCHEMA_V13);

    // connect hooks
    this.setupTableHooks(this.tables);
    this.trainingDownloadStatusListener();
  }

  clearData() {
    // assessmentsV2 is excluded so grand quiz completion survives logout/re-login.
    // The server-side sync for grandQuizzes can be delayed, making assessmentsV2
    // the reliable source of truth (as noted in isLevelAssessmentPassed logic).
    this.tables
      .filter((table) => table.name !== 'assessmentsV2')
      .forEach((table) => table.clear());
  }

  private async getCurrentNormalizedProfileId(): Promise<string | undefined> {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles?.find((p) => p.isSelected);
    if (!profile?.participantId) return undefined;
    return normalizeProfileId(profile.participantId);
  }

  /**
   * Returns the active/selected profile's RAW participant UUID (hyphenated), or
   * undefined if there is no participant. Unlike getCurrentNormalizedProfileId,
   * this does NOT normalize — the raw UUID is the form stored in the `participant`
   * field on trainingStatusV2 rows, so callers stamping participant must use it.
   * Used to close Gap 1: ensure status rows are written with a participant even
   * when the caller omits one.
   */
  private async getActiveParticipantId(): Promise<string | undefined> {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles?.find((p) => p.isSelected);
    return profile?.participantId ?? undefined;
  }

  async getCourses(): Promise<CourseRecord[]> {
    const profileId = await this.getCurrentNormalizedProfileId();
    if (!profileId) return [];
    const records = await this.coursesV2
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          normalizeProfileId(record.profileId ?? '') === profileId,
      )
      .toArray();
    return records.map(stripCourseThumbnailBase64);
  }
  getCoursesbyParticipantId(
    participantId: string,
  ): PromiseExtended<CourseRecord[]> {
    const normalizedSearchId = normalizeProfileId(participantId);

    return this.coursesV2
      .filter((record) => {
        if (record.syncStatus === SyncRecordStatus.Deleted) {
          return false;
        }
        return (
          normalizeProfileId(record.profileId ?? '') === normalizedSearchId
        );
      })
      .sortBy('id') // ✅ Observable chain (not .toArray())
      .then((records) => {
        return records.map(stripCourseThumbnailBase64);
      }) as PromiseExtended<CourseRecord[]>;
  }
  async getCourse(id: number): Promise<CourseRecord | undefined> {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles?.find((p) => p.isSelected);
    if (!profile?.participantId) return undefined;
    const profileId = profile.participantId;
    return this.coursesV2
      .filter(
        (record) =>
          record.id === id &&
          normalizeProfileId(record.profileId ?? '') ===
            normalizeProfileId(profileId) &&
          record.syncStatus !== SyncRecordStatus.Deleted,
      )
      .first();
  }
  async getCoursesByLevel(levelValue: number): Promise<CourseRecord[]> {
    const profileId = await this.getCurrentNormalizedProfileId();
    if (!profileId) return [];
    const records = await this.coursesV2
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.level?.value === levelValue &&
          normalizeProfileId(record.profileId ?? '') === profileId,
      )
      .toArray();
    return records.map(stripCourseThumbnailBase64);
  }
  async getLevelAssessmentById(
    id: number,
  ): Promise<GrandQuizzesRecord | undefined> {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles?.find((p) => p.isSelected);
    if (!profile?.participantId) return undefined;
    const profileId = profile.participantId;
    return this.grandQuizzes
      .filter(
        (record) =>
          record.id === id &&
          normalizeProfileId(record.profileId ?? '') ===
            normalizeProfileId(profileId),
      )
      .first();
  }
  async getLevelAssessmentByType(
    type: LevelAssessmentType,
    level: number,
  ): Promise<GrandQuizzesRecord[]> {
    const profileId = await this.getCurrentNormalizedProfileId();
    if (!profileId) return [];
    return this.grandQuizzes
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.type === type &&
          record.level === level &&
          normalizeProfileId(record.profileId ?? '') === profileId,
      )
      .toArray();
  }
  async getAllLevelValues(): Promise<number[]> {
    const courses = await this.getCourses();

    const levelValues = courses
      .map((course) => course.level?.value)
      .filter((value): value is number => value !== undefined);

    return Array.from(new Set(levelValues)).sort((a, b) => a - b);
  }
  async getSelectedCategoryCourses(type: string): Promise<ICourse[]> {
    return (await this.getCourses()).filter((course) => course.type === type);
  }

  async getSubjectCategory(type: string) {
    const data: ICourse[] = await this.getSelectedCategoryCourses(type);
    const subjects = data.map((course) => course.subject);
    const uniqueSubjects = Array.from(new Set(subjects)) as number[];
    return uniqueSubjects;
  }
  async getTraining(id: number): Promise<TrainingRecord | undefined> {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles.find((p) => p.isSelected);
    const profileId = profile?.participantId ?? '';
    return this.trainingsV2
      .filter(
        (record) =>
          record.id === id &&
          normalizeProfileId(record.profileId ?? '') ===
            normalizeProfileId(profileId) &&
          record.syncStatus !== SyncRecordStatus.Deleted,
      )
      .first();
  }

  /**
   * DEPRECATED: Use getTrainingsByCourse() instead.
   * This method does NOT filter by current profile, causing duplicate trainings
   * when multiple profiles exist in the same IndexedDB.
   */
  getTrainings(courseId: number): PromiseExtended<TrainingRecord[]> {
    return this.trainingsV2
      .where('course')
      .equals(courseId)
      .filter(
        (record: TrainingRecord) =>
          record.syncStatus !== SyncRecordStatus.Deleted,
      )
      .sortBy('index');
  }

  getAllTrainings(): PromiseExtended<TrainingRecord[]> {
    return this.trainingsV2
      .filter(
        (record: TrainingRecord) =>
          record.syncStatus !== SyncRecordStatus.Deleted,
      )
      .toArray();
  }

  getTrainingsByParticipantId(participantId: string) {
    const normalizedId = normalizeProfileId(participantId);
    return this.trainingsV2
      .filter((r: TrainingRecord) => {
        const match =
          r.syncStatus !== SyncRecordStatus.Deleted &&
          normalizeProfileId(r.profileId ?? '') === normalizedId;
        return match;
      })
      .toArray();
  }

  async getTrainingsByCourse(courseId: number): Promise<TrainingRecord[]> {
    const profileId = await this.getCurrentNormalizedProfileId();
    if (!profileId) return [];
    return this.trainingsV2
      .filter(
        (record: TrainingRecord) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.course === courseId &&
          normalizeProfileId(record.profileId ?? '') === profileId,
      )
      .sortBy('index');
  }

  getTrainingsByCourseForParticipant(courseId: number, participantId: string) {
    const normalizedId = normalizeProfileId(participantId);
    return this.trainingsV2
      .filter(
        (record: TrainingRecord) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.course === courseId &&
          normalizeProfileId(record.profileId ?? '') === normalizedId,
      )
      .sortBy('index');
  }

  async getDownloadedTrainings(): Promise<TrainingRecord[]> {
    const profileId = await this.getCurrentNormalizedProfileId();
    if (!profileId) return [];
    return this.trainingsV2
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          normalizeProfileId(record.profileId ?? '') === profileId &&
          record.mediaAsset !== undefined &&
          record.mediaAsset?.download?.status === DownloadStatus.Completed,
      )
      .sortBy('index');
  }

  trainingDownloadStatusListener() {
    downloadManagerService.onDownload((data) => {
      if (data.tag.modelDownloadName === ModelDownloadName.Training) {
        this.trainingEventListener(data);
      }
    });
  }
  async trainingEventListener(downloadInfo: DownloadInfo) {
    // Handle COMPLETED status
    if (downloadInfo.status === DownloadStatus.Completed) {
      await this.updateDownloadStatus(downloadInfo);
    }
    if (downloadInfo.status === DownloadStatus.Failed) {
      await this.updateDownloadStatus(downloadInfo);
    }
  }

  async updateDownloadStatus(downloadInfo: DownloadInfo) {
    const request = {
      id: downloadInfo.id,
      url: downloadInfo.url,
      downloadId: downloadInfo.id,
      download: downloadInfo,
      modelDownloadRequestId: downloadInfo.tag.modelDownloadRequestId,
      mediaType: MediaType.Video,
      timestamp: Date.now(),
      modelDownloadName: ModelDownloadName.Training,
    };
    const training = await this.getTraining(
      downloadInfo.tag.modelDownloadRequestId,
    );
    downloadRequestModel.transaction(
      'rw',
      downloadRequestModel.downloads,
      async () => {
        const downloadObject =
          await downloadRequestModel.getDownloadRequestByTrainingURL(
            downloadInfo.url,
          );
        await downloadRequestModel.addDownloadRequest(request);
        if (
          downloadInfo.status === DownloadStatus.Completed &&
          downloadObject?.download?.status !== DownloadStatus.Completed
        ) {
          analyticsService.trackEvent(EventNames.trainingDownloadComplete, {
            epTrainingId: downloadInfo.tag.modelDownloadRequestId,
            epCourseId: training?.course ?? null,
          });
        }
        if (
          downloadInfo.status === DownloadStatus.Failed &&
          downloadObject?.download?.status !== DownloadStatus.Failed
        ) {
          analyticsService.trackEvent(EventNames.trainingDownloadError, {
            epTrainingId: downloadInfo.tag.modelDownloadRequestId,
            epCourseId: training?.course ?? null,
            epErrorMessage: downloadInfo.error,
          });
        }
      },
    );
  }

  async updateTrainingsWithDownloads(downloads: DownloadInfo[]) {
    const questions = await this.questions.toArray();
    const downloadMap = new Map(
      downloads.map((download) => [download.url, download]),
    );
    const questionsIds = new Set<number>();

    const downloadRequests = [];
    for (const asset of downloads) {
      const download = downloadMap.get(asset.url);
      if (
        download &&
        download.tag.modelDownloadName === ModelDownloadName.Training
      ) {
        downloadRequests.push({
          id: download.id,
          url: download.url,
          downloadId: download.id,
          download: download,
          modelDownloadRequestId: download.tag.modelDownloadRequestId,
          mediaType: MediaType.Video,
          timestamp: Date.now(),
          modelDownloadName: ModelDownloadName.Training,
        });
        const hasQuestion = questions.filter(
          (question) =>
            question.training === download.tag.modelDownloadRequestId,
        );

        if (hasQuestion.length === 0 && (await Network.getStatus()).connected)
          questionsIds.add(download.tag.modelDownloadRequestId);
      }
    }
    for (const questionId of questionsIds) {
      await fetchTrainingQuestions(questionId, null);
    }

    downloadRequestModel.addBulkDownloadRequest(downloadRequests);
  }

  getQuestionsCount(trainingId: number): PromiseExtended<number> {
    return this.questions
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.training === trainingId,
      )
      .count();
  }

  getQuestions(trainingId: number): PromiseExtended<TrainingQuestionRecord[]> {
    return this.questions
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.training === trainingId &&
          (record.type === QuestionType.Mcq ||
            record.type === QuestionType.Msq ||
            record.type === QuestionType.OpenEnded ||
            record.type === QuestionType.Poll),
      )
      .sortBy('index');
  }

  async setSubmission(
    training: number | null,
    answer: string,
    question: number,
    isCorrect: boolean,
    questionType: string,
    questionCopy: ITrainingQuestion,
    score: number,
    grandQuiz: number | null,
    participant?: string,
    feedback?: string,
  ) {
    // Gap 8 fix: never write a submission row with participant=undefined.
    // createHook stamps numeric `profile` FK for Teacher only; Coach/Principal
    // writes get neither `profile` nor `participant` and become fully orphaned.
    // Resolve the effective participant UUID from the active profile when the
    // caller did not pass one (same pattern as the Gap-1 fix in
    // setTrainingStatusToComplete).
    const effectiveParticipant =
      participant ?? (await this.getActiveParticipantId());
    const newSubmission: SubmissionRecord = {
      training,
      answer,
      question,
      isCorrect,
      questionType,
      questionCopy,
      score,
      grandQuiz,
      participant: effectiveParticipant,
      feedback,
    };
    await this.submissionsV2.add(newSubmission);
  }

  async setTrainingAssessment(
    training: number | null,
    grandQuiz: number | null,
    score: number,
    totalScore: number,
    isPassed: boolean,
    participant?: string,
    assessmentType?: LevelAssessmentType,
    courseId?: number,
    levelId?: number,
  ) {
    // Gap 8 fix: never write an assessment row with participant=undefined.
    // createHook stamps numeric `profile` FK for Teacher only; Coach/Principal
    // writes get neither `profile` nor `participant` and become fully orphaned.
    // Resolve the effective participant UUID from the active profile when the
    // caller did not pass one (same pattern as the Gap-1 fix in
    // setTrainingStatusToComplete).
    const effectiveParticipant =
      participant ?? (await this.getActiveParticipantId());
    const newAssessment: AssessmentRecord = {
      training: training,
      score: score,
      grandQuiz: grandQuiz,
      totalScore: totalScore,
      isPassed: isPassed,
      participant: effectiveParticipant,
      profileId: effectiveParticipant
        ? normalizeProfileId(effectiveParticipant)
        : undefined,
      syncStatus: SyncRecordStatus.Created,
    };
    if (grandQuiz !== null) {
      await this.setGrandQuizStatus(
        grandQuiz,
        isPassed,
        score,
        assessmentType,
        effectiveParticipant,
        levelId,
      );
    }

    // this id is different from the one generated on backend, on frontend we have auto increment which will clash from backend.
    // this means the id changes when the record is created on the backend
    const assessmentId = await this.assessmentsV2.add(newAssessment);
    const addedAssessment = await this.assessmentsV2.get(assessmentId);
    const idToSearch = training === null ? grandQuiz : training;
    const user = await userDB.getUserProfileFromDB();
    // Prefer exact participant match (resolved above), fall back to selected profile
    const profile = effectiveParticipant
      ? (user?.profiles?.find(
          (p) =>
            normalizeProfileId(p.participantId) ===
            normalizeProfileId(effectiveParticipant),
        ) ?? user?.profiles?.find((p) => p.isSelected))
      : user?.profiles?.find((p) => p.isSelected);
    const profileId = profile?.participantId ?? '';
    const currentTraining =
      idToSearch === null
        ? undefined
        : await this.trainingsV2
            .filter(
              (record) =>
                record.id === idToSearch &&
                normalizeProfileId(record.profileId ?? '') ===
                  normalizeProfileId(profileId),
            )
            .first();

    // only update the training record if the new assessment has a higher score
    if (
      !currentTraining?.assessment ||
      score > currentTraining.assessment.score
    ) {
      if (
        idToSearch !== null &&
        currentTraining === undefined &&
        courseId != null
      ) {
        await this.trainingsV2.add({
          id: idToSearch,
          index: 0,
          title: '',
          description: '',
          isGrandAssessment: grandQuiz !== null,
          course: courseId,
          assessment: addedAssessment,
          trainingStatus: null,
          profileId,
          syncStatus: SyncRecordStatus.Updated,
        } as TrainingRecord);
      } else {
        const existingTraining = await this.trainingsV2
          .filter(
            (record) =>
              record.id === idToSearch! &&
              normalizeProfileId(record.profileId ?? '') ===
                normalizeProfileId(profileId),
          )
          .toArray();
        if (existingTraining.length > 0) {
          await this.trainingsV2.put({
            ...existingTraining[0],
            assessment: addedAssessment ?? null,
            syncStatus: SyncRecordStatus.Updated,
          });
        }
      }
    }
  }

  async getGrandQuizStatus(
    level: number,
    type: LevelAssessmentType = LevelAssessmentType.GrandQuiz,
  ): Promise<IGrandQuizzes | undefined> {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles?.find((p) => p.isSelected);
    if (!profile?.participantId) return undefined;
    const profileId = profile.participantId;
    return this.grandQuizzes
      .filter(
        (record) =>
          record.level === level &&
          record.type === type &&
          normalizeProfileId(record.profileId ?? '') ===
            normalizeProfileId(profileId),
      )
      .first();
  }
  async getGrandQuiz(): Promise<IGrandQuizzes[]> {
    const profileId = await this.getCurrentNormalizedProfileId();
    if (!profileId) return [];
    return this.grandQuizzes
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          normalizeProfileId(record.profileId ?? '') === profileId,
      )
      .toArray();
  }

  getGrandQuizByParticipant(
    participantId: string,
  ): PromiseExtended<GrandQuizzesRecord[]> {
    const normalizedSearchId = normalizeProfileId(participantId);

    return this.grandQuizzes
      .filter((record) => {
        if (record.syncStatus === SyncRecordStatus.Deleted) {
          return false;
        }
        return (
          normalizeProfileId(record.profileId ?? '') === normalizedSearchId
        );
      })
      .toArray();
  }
  async setGrandQuizStatus(
    grandQuizId: number,
    isPassed: boolean,
    obtainedScore: number,
    assessmentType?: LevelAssessmentType,
    participantId?: string,
    levelId?: number,
  ) {
    let profileId: string;
    if (participantId) {
      profileId = participantId;
    } else {
      const user = await userDB.getUserProfileFromDB();
      const profile = user?.profiles?.find((p) => p.isSelected);
      if (!profile?.participantId) return;
      profileId = profile.participantId;
    }

    const statusRecordFromLevel = await this.grandQuizzes
      .filter(
        (record) =>
          record.id === grandQuizId &&
          normalizeProfileId(record.profileId ?? '') ===
            normalizeProfileId(profileId),
      )
      .toArray();
    const grandQuizStatus = statusRecordFromLevel[0]?.assessments;
    const modifyPayload: Record<string, any> = {
      assessments:
        grandQuizStatus == null
          ? [
              {
                id: 1,
                grandQuiz: grandQuizId,
                isPassed: isPassed,
                attemptNumber: 1,
                score: obtainedScore,
                lastAttemptedAt: new Date().toISOString(),
              },
            ]
          : [
              ...grandQuizStatus,
              {
                id: grandQuizStatus[grandQuizStatus.length - 1].id + 1,
                isPassed: isPassed,
                grandQuiz: grandQuizId,
                attemptNumber:
                  grandQuizStatus[grandQuizStatus.length - 1].attemptNumber + 1,
                score:
                  obtainedScore >
                  grandQuizStatus[grandQuizStatus.length - 1].score
                    ? obtainedScore
                    : grandQuizStatus[grandQuizStatus.length - 1].score,
                lastAttemptedAt: new Date().toISOString(),
              },
            ],
    };

    if (assessmentType) {
      modifyPayload.type = assessmentType;
    }

    if (statusRecordFromLevel.length === 0) {
      // No record exists yet — create a surrogate so the live query reacts immediately.
      // id = grandQuizId (real PK), level = levelId (if provided) so lookups by level work.
      await this.grandQuizzes.add({
        id: grandQuizId,
        level: levelId ?? grandQuizId,
        profileId,
        syncStatus: SyncRecordStatus.Created,
        ...modifyPayload,
      } as GrandQuizzesRecord);
    } else {
      const updatedRecord = {
        ...statusRecordFromLevel[0],
        ...modifyPayload,
        syncStatus: SyncRecordStatus.Updated,
      };
      await this.grandQuizzes.put(updatedRecord);
    }
  }
  /**
   * sets the training status for a specific training and course.
   * the function performs the following steps:
   * 1. fetches the TrainingStatusRecord for the given trainingId and courseId.
   * 2. if the TrainingStatusRecord exists and its status is either PENDING or COMPLETED, the function returns without making any changes.
   * 3. if the TrainingStatusRecord does not exist, it creates a new TrainingStatusRecord with status set to IN_PROGRESS and updates the corresponding TrainingRecord's trainingStatus to IN_PROGRESS.
   * does not make any changes if the status is already IN_PROGRESS or COMPLETED.
   */
  async setTrainingStatusToInProgress(
    trainingId: number,
    courseId: number,
    participant?: string,
  ) {
    const trainingStatusRecord = await this.getTrainingStatusRecord(
      trainingId,
      courseId,
      participant,
    );
    if (
      trainingStatusRecord &&
      (trainingStatusRecord.status === ProgressStatus.InProgress ||
        trainingStatusRecord.status === ProgressStatus.Completed)
    ) {
      await this.trainingStatusV2.put({
        ...trainingStatusRecord,
        lastModifiedAt: Date.now(),
      });
      return;
    }
    if (!trainingStatusRecord) {
      // Guard: if the training record itself is already Completed (e.g. status record
      // was lost or belongs to a different profile key), do not downgrade to InProgress.
      const user = await userDB.getUserProfileFromDB();
      const profile = participant
        ? (user?.profiles?.find(
            (p) =>
              normalizeProfileId(p.participantId) ===
              normalizeProfileId(participant),
          ) ?? user?.profiles?.find((p) => p.isSelected))
        : user?.profiles?.find((p) => p.isSelected);
      const profileId = profile?.participantId ?? '';
      const trainingRecord = await this.trainingsV2
        .filter(
          (r) =>
            r.id === trainingId &&
            normalizeProfileId(r.profileId ?? '') ===
              normalizeProfileId(profileId),
        )
        .first();
      if (trainingRecord?.trainingStatus === ProgressStatus.Completed) {
        return;
      }
      // Gap 1 fix: stamp the resolved participant UUID (profileId, derived above
      // from the matched/selected profile) so the row is attributable on push and
      // visible to participant-scoped pulls on other devices. Prefer the explicit
      // argument; fall back to the resolved active-profile UUID.
      await this.trainingStatusV2.add({
        training: trainingId,
        courseId,
        status: ProgressStatus.InProgress,
        participant: participant ?? (profileId || undefined),
        lastModifiedAt: Date.now(),
      });
      await this.updateTrainingStatusInTrainingRecord(
        trainingId,
        ProgressStatus.InProgress,
        courseId,
        participant,
      );
    }
  }

  /**
   * sets the training status for a specific training and course to "COMPLETED".
   * the function performs the following steps:
   * 1. fetches the TrainingStatusRecord for the given trainingId and courseId.
   * 2. if the TrainingStatusRecord exists and its status is COMPLETED, the function returns without making any changes.
   * 3. if the TrainingStatusRecord does not exist or its status is not COMPLETED, it creates/updates a TrainingStatusRecord with status set to COMPLETED
   * and updates the corresponding TrainingRecord's trainingStatus to "COMPLETED".
   */
  async setTrainingStatusToComplete(
    trainingId: number,
    courseId: number,
    participant?: string,
  ) {
    const trainingStatusRecord = await this.getTrainingStatusRecord(
      trainingId,
      courseId,
      participant,
    );
    if (
      trainingStatusRecord &&
      trainingStatusRecord.status === ProgressStatus.Completed
    ) {
      return;
    }
    if (!trainingStatusRecord) {
      // Gap 1 fix: never write a status row with participant=undefined. The Dexie
      // createHook stamps the numeric `profile` FK but NOT the UUID `participant`,
      // so an unstamped row pushes with only `profile` and is invisible to
      // participant-scoped pull queries on other devices until the server backfill
      // runs. Resolve the effective participant UUID from the active profile when
      // the caller did not pass one.
      const effectiveParticipant =
        participant ?? (await this.getActiveParticipantId());
      await this.trainingStatusV2.add({
        training: trainingId,
        courseId,
        status: ProgressStatus.Completed,
        participant: effectiveParticipant,
        syncStatus: SyncRecordStatus.Created,
      });
    } else {
      await this.trainingStatusV2.put({
        ...trainingStatusRecord,
        status: ProgressStatus.Completed,
        syncStatus: SyncRecordStatus.Updated,
      });
    }
    await this.updateTrainingStatusInTrainingRecord(
      trainingId,
      ProgressStatus.Completed,
      courseId,
      participant,
    );
  }
  async setAllTrainingStatusToCompleted(level: number, participant?: string) {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles?.find((p) => p.isSelected);
    if (!profile?.participantId) return;
    const profileId = profile.participantId;
    const courses = await this.coursesV2.toArray();
    const levelCourses = courses.filter(
      (course) =>
        course.level?.value === level &&
        normalizeProfileId(course.profileId ?? '') ===
          normalizeProfileId(profileId),
    );
    for (const course of levelCourses) {
      // Safe: course.profileId comes from the record fetched above, so the
      // compound key [profileId+id] is guaranteed to match. Soft-deleted or
      // Read-first-put pattern ensures observable subscription is triggered
      const courseToUpdate = await this.coursesV2
        .where('[profileId+id]')
        .equals([course.profileId ?? '', course.id])
        .first();
      if (courseToUpdate) {
        await this.coursesV2.put({
          ...courseToUpdate,
          courseCompletionPercentage: 100,
          syncStatus: SyncRecordStatus.Updated,
        });
      }
    }
    for (const course of levelCourses) {
      const trainings = await this.trainingsV2
        .filter(
          (r) =>
            r.course === course.id &&
            normalizeProfileId(r.profileId ?? '') ===
              normalizeProfileId(profileId),
        )
        .toArray();
      for (const training of trainings) {
        await this.setTrainingStatusToComplete(
          training.id,
          course.id,
          participant,
        );
      }
    }
  }
  async updateCourseCompletionIfAllDone(
    courseId: number,
    profileId: string,
  ): Promise<void> {
    const percentage = await this.getCourseCompletionPercentage(
      courseId,
      profileId,
    );
    // Use filter+normalizeProfileId (not compound key .equals) — the stored
    // profileId may differ in dash/case format from the caller's participantId.
    const normalizedProfileId = normalizeProfileId(profileId);
    const existing = await this.coursesV2
      .filter(
        (c) =>
          Number(c.id) === Number(courseId) &&
          normalizeProfileId(c.profileId ?? '') === normalizedProfileId,
      )
      .toArray();
    if (existing.length === 0) {
      console.warn(
        '[updateCourseCompletionIfAllDone] course record not found in Dexie — skipping modify',
        { courseId, profileId },
      );
      return;
    }
    await this.coursesV2.put({
      ...existing[0],
      courseCompletionPercentage: percentage,
      syncStatus: SyncRecordStatus.Updated,
    });
  }

  async getCourseCompletionPercentage(
    courseId: number,
    profileId?: string,
  ): Promise<number> {
    let actualProfileId = profileId;
    if (!actualProfileId) {
      const user = await userDB.getUserProfileFromDB();
      const profile = user?.profiles?.find((p) => p.isSelected);
      actualProfileId = profile?.participantId ?? '';
    }
    const normalizedProfileId = normalizeProfileId(actualProfileId ?? '');
    // Use Number() coercion on both sides — Dexie may store course as string if
    // the pull-sync API response contained a stringified integer.
    const trainings = await this.trainingsV2
      .filter(
        (r) =>
          Number(r.course) === Number(courseId) &&
          normalizeProfileId(r.profileId ?? '') === normalizedProfileId,
      )
      .toArray();
    const completedTrainings = trainings.filter(
      (t) => t.trainingStatus === ProgressStatus.Completed,
    ).length;
    const percentage =
      trainings.length > 0 ? (completedTrainings / trainings.length) * 100 : 0;
    return Math.round(percentage);
  }

  /**
   * determines the next training and course for a user based on their progress.
   *
   * - First Time User: if no training statuses are present, it indicates a new user.
   *   fetches the first training of the first course. Sets CTA as "Start Training"
   *   and progress text as "Let’s start your first training!". indicates all
   *   trainings are not completed.
   *
   * - Existing User with IN_PROGRESS Training: if there are training statuses,
   *   fetches the latest based on the `lastModifiedAt` field. if the latest training
   *   status is IN_PROGRESS, retrieves the corresponding course and training.
   *   Sets CTA as "Resume Training" and progress text as "Let’s continue where
   *   you left!". Indicates all trainings are not completed.
   *
   * - User Who Completed Latest Training: ff the latest training is completed,
   *   finds the next incomplete training in the current course. Sets CTA as
   *   "Resume Training" and progress text as before.
   *
   * - All Trainings in Current Course Completed: if all trainings in the current
   *   course are done, moves to the next course to find the first incomplete
   *   training. If the current course is a general course, it finds the next general course.
   *   If it's a subject course, it finds the next subject course. If all trainings in the
   *   current group (general or subject) are completed, it moves to the other group.
   *   Sets CTA and progress text as in the previous case.
   *
   * - All Courses and Trainings Completed: if all courses and their trainings are
   *   completed, returns an indication that all trainings are completed.
   *
   */
  async getNextTrainingAndCourse(): Promise<TrainingCourseProgressInfo | null> {
    return teacherTrainingService.getNextTrainingAndCourse();
  }

  private async getTrainingStatusRecord(
    trainingId: number,
    courseId: number,
    participantId?: string,
  ): Promise<TrainingStatusRecord | undefined> {
    return this.trainingStatusV2
      .filter((r) => {
        if (r.training !== trainingId || r.courseId !== courseId) return false;
        if (!participantId) return true;
        return (
          normalizeProfileId(r.participant ?? '') ===
          normalizeProfileId(participantId)
        );
      })
      .first();
  }

  private async updateTrainingStatusInTrainingRecord(
    trainingId: number,
    status: ProgressStatus,
    courseId = 0,
    participantId?: string,
  ) {
    const user = await userDB.getUserProfileFromDB();
    const profile = participantId
      ? (user?.profiles?.find(
          (p) =>
            normalizeProfileId(p.participantId) ===
            normalizeProfileId(participantId),
        ) ?? user?.profiles?.find((p) => p.isSelected))
      : user?.profiles?.find((p) => p.isSelected);
    if (!profile?.participantId) return;
    const profileId = profile.participantId;
    const existing = await this.trainingsV2
      .filter(
        (record) =>
          record.id === trainingId &&
          normalizeProfileId(record.profileId ?? '') ===
            normalizeProfileId(profileId),
      )
      .toArray();
    if (existing.length === 0) {
      // Record not yet seeded from server sync — create a minimal placeholder
      // so the status is not silently lost. Missing fields use safe defaults.
      await this.trainingsV2.add({
        id: trainingId,
        index: 0,
        title: '',
        description: '',
        isGrandAssessment: false,
        course: courseId,
        assessment: null,
        trainingStatus: status,
        profileId,
        syncStatus: SyncRecordStatus.Updated,
      } as TrainingRecord);
    } else {
      const updatedRecord = {
        ...existing[0],
        trainingStatus: status,
        syncStatus: SyncRecordStatus.Updated,
      };
      await this.trainingsV2.put(updatedRecord);
    }
  }

  private async getTeacherTrainingSyncData(
    model: Table,
  ): Promise<SyncRecord[]> {
    const records = await model
      .where('syncStatus')
      .anyOf([
        SyncRecordStatus.Created,
        SyncRecordStatus.Updated,
        SyncRecordStatus.Failed,
      ])
      .toArray();

    // Normalize both Updated and Failed to Created so every pushable
    // teacher_training_status row lands in the `created` bucket. Failed rows are
    // ones the server previously rejected — including them here is what gets them
    // retried on the next push instead of silently never re-pushing (bug-s7 parity).
    return records.map((record) => {
      if (
        record.syncStatus === SyncRecordStatus.Updated ||
        record.syncStatus === SyncRecordStatus.Failed
      ) {
        return { ...record, syncStatus: SyncRecordStatus.Created };
      }
      return record;
    });
  }

  async getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>> {
    const records = {
      assessment: await this._getSyncData(this.assessmentsV2),
      submission: await this._getSyncData(this.submissionsV2),
      teacherTrainingStatus: await this.getTeacherTrainingSyncData(
        this.trainingStatusV2,
      ),
    };
    return records;
  }

  async updateRecordsSyncStatus(
    syncChanges: SyncChanges,
    status: SyncRecordStatus,
  ): Promise<number> {
    let updates = 0;
    const idsToMarkSynced = {
      assessmentsV2: syncChanges.assessment?.created?.map(
        (record: { id: number }) => record.id,
      ),
      submissionsV2: syncChanges.submission?.created?.map(
        (record: { id: number }) => record.id,
      ),
      trainingStatusV2: syncChanges.teacherTrainingStatus?.created?.map(
        (record: { id: string }) => record.id,
      ),
    };

    const idsToMarkSyncedFromUpdated = {
      trainingStatusV2: syncChanges.teacherTrainingStatus?.updated?.map(
        (record: { id: string }) => record.id,
      ),
    };

    for (const [model, ids] of Object.entries(idsToMarkSynced)) {
      if (!ids) {
        continue;
      }
      updates += await this._updateSyncData(
        // @ts-ignore
        this[model],
        ids,
        status,
      );
    }

    for (const [model, ids] of Object.entries(idsToMarkSyncedFromUpdated)) {
      if (!ids) {
        continue;
      }
      updates += await this._updateSyncData(
        // @ts-ignore
        this[model],
        ids,
        status,
      );
    }

    return 1;
  }
  async getGrandQuizData(): Promise<IGrandQuizzes[]> {
    const profileId = await this.getCurrentNormalizedProfileId();
    if (!profileId) return [];
    return this.grandQuizzes
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          normalizeProfileId(record.profileId ?? '') === profileId,
      )
      .toArray();
  }
  async getLevelInfo(level: number): Promise<LevelsRecord | undefined> {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles?.find((p) => p.isSelected);
    if (!profile?.participantId) return undefined;
    const profileId = profile.participantId;
    return this.levels
      .filter(
        (record) =>
          record.id === level &&
          normalizeProfileId(record.profileId ?? '') ===
            normalizeProfileId(profileId),
      )
      .first();
  }

  async issueCertificate(
    courseId: string,
    vendor: string,
    teacherName: string,
    trainingName: string,
    participantId?: string,
  ): Promise<void> {
    const user = await userDB.getUserProfileFromDB();
    const profile = user?.profiles?.find((p) => p.isSelected);
    const profileId = profile?.participantId;

    if (!profileId && !participantId) return;

    const VENDOR_METADATA_DEFAULTS: Record<string, CertificateMetadata> = {
      Oxbridge: OXBRIDGE_METADATA_DEFAULTS,
      OXBRIDGE: OXBRIDGE_METADATA_DEFAULTS,
      Beaconhouse: BEACONHOUSE_METADATA_DEFAULTS,
      BEACONHOUSE: BEACONHOUSE_METADATA_DEFAULTS,
      I_SAPS: I_SAPS_METADATA_DEFAULTS,
    };

    const VENDOR_PREFIXES: Record<string, string> = {
      Oxbridge: VENDOR_CODE_PREFIXES.OXBRIDGE,
      OXBRIDGE: VENDOR_CODE_PREFIXES.OXBRIDGE,
      Beaconhouse: VENDOR_CODE_PREFIXES.BEACONHOUSE,
      BEACONHOUSE: VENDOR_CODE_PREFIXES.BEACONHOUSE,
      I_SAPS: VENDOR_CODE_PREFIXES.I_SAPS,
    };

    const prefix = VENDOR_PREFIXES[vendor] ?? 'TL';
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.Z]/g, '')
      .slice(0, 12);
    const randomHex = generateSecureRandomCode();
    const certificateCode = `${prefix}-${timestamp}-${randomHex}`;

    const certificate: Certificate = {
      id: uuidv4(),
      course: courseId,
      participant: participantId,
      profile: profileId,
      vendor: vendor as any,
      teacherName,
      trainingName,
      completionDate: new Date().toISOString().split('T')[0],
      issuedAt: new Date().toISOString(),
      certificateCode,
      metadata:
        VENDOR_METADATA_DEFAULTS[vendor] ||
        VENDOR_METADATA_DEFAULTS['Oxbridge'],
      syncStatus: SyncRecordStatus.Created,
      modifiedAt: new Date().toISOString(),
      isActive: true,
    };

    await this.certificates.add(certificate);
  }

  async getCertificatesForProfile(
    participantId: string,
  ): Promise<Certificate[]> {
    const normalizedId = normalizeProfileId(participantId);
    return this.certificates
      .filter(
        (record) =>
          normalizeProfileId(record.participant ?? '') === normalizedId ||
          normalizeProfileId(record.profile ?? '') === normalizedId,
      )
      .toArray();
  }
}

export const teacherTrainingDB = new TeacherTrainingDB();
